import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderFieldForm, renderTextArea } from '../../shared/formComponents';
import { GOOGLE_SALES_COUNTRY_OPTIONS } from '../../shared/constants';
import { searchGoogleLanguageConstants, getGoogleLanguageConstantsByResourceNames } from '../../shared/budgetApi';
import GeoTargetingFields from './shared/GeoTargetingFields';
import GoogleConstantMultiSelect from './shared/GoogleConstantMultiSelect';

/**
 * Google Budget Fields Component
 * Handles all 3 Google ad types: Search, Performance Max, Standard Shopping
 */
const GoogleBudgetFields = ({
  budgetTypeOption,
  setupFlow,
  agencyInternal,
  formValues,
  itemCsvInput,
}) => {
  if (!budgetTypeOption) return null;

  const { value: adType, fields } = budgetTypeOption;

  // URL validation helper
  const isValidUrl = (urlString) => {
    if (!urlString) return null; // null = no validation shown
    try {
      const url = new URL(urlString.trim());
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // ASIN validation helper
  const validateAsins = (asinString) => {
    if (!asinString) return { valid: true, asins: [] };
    const asins = asinString.split(',').map(item => item.trim()).filter(item => item);
    const invalidAsins = asins.filter(asin => !asin.toUpperCase().startsWith('B0') || asin.length !== 10);
    return {
      valid: invalidAsins.length === 0,
      asins,
      invalidAsins,
    };
  };

  // Product ID validation helper
  const validateProductIds = (idString) => {
    if (!idString) return { valid: true, ids: [] };
    const ids = idString.split(',').map(item => item.trim()).filter(item => item);
    return {
      valid: ids.length > 0,
      ids,
      count: ids.length,
    };
  };

  // Get validation results
  const urlValidation = fields?.url ? isValidUrl(formValues?.masterItemOnList) : null;
  const asinValidation = fields?.asins ? validateAsins(formValues?.masterItemOffList) : null;
  const productIdValidation = fields?.productIds ? validateProductIds(formValues?.masterItemOnList) : null;

  return (
    <>
      {/* URL Field for Search and PMax */}
      {fields?.url && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            {adType === 'google_search' ? 'Landing Page URL' : 'Store URL'}{' '}
            <Tooltip
              id="googleUrl"
              msg={adType === 'google_search' ? 'Enter the landing page URL for your Search ads' : 'Enter your store URL for Performance Max campaigns'}
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="masterItemOnList"
              component={renderFieldForm}
              placeholder="https://..."
            />
          </div>
          {formValues?.masterItemOnList && urlValidation !== null && (
            <div
              style={{
                marginTop: '10px',
                padding: '10px',
                borderRadius: '4px',
                border: `1px solid ${urlValidation ? 'rgba(145, 190, 35, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`,
                backgroundColor: `${urlValidation ? 'rgba(145, 190, 35, 0.1)' : 'rgba(255, 0, 0, 0.1)'}`,
              }}
            >
              <div style={{ fontWeight: '500', color: urlValidation ? '#91BE23' : '#ff4861' }}>
                {urlValidation ? '✓ Valid URL' : '⚠ Invalid URL'}
              </div>
              {!urlValidation && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  URL must start with http:// or https://
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ASINs Field for Search only */}
      {fields?.asins && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Target ASINs (Optional){' '}
            <Tooltip
              id="googleAsins"
              msg="Enter comma-separated ASINs to target specific products (must start with B0 and be 10 characters)"
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="masterItemOffList"
              component={renderTextArea}
              format={itemCsvInput}
              placeholder="B0ABC123, B0DEF456, B0GHI789"
            />
          </div>
          {formValues?.masterItemOffList && asinValidation && (
            <div
              style={{
                marginTop: '10px',
                padding: '10px',
                borderRadius: '4px',
                border: `1px solid ${asinValidation.valid ? 'rgba(145, 190, 35, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`,
                backgroundColor: `${asinValidation.valid ? 'rgba(145, 190, 35, 0.1)' : 'rgba(255, 0, 0, 0.1)'}`,
              }}
            >
              <div style={{ fontWeight: '500', color: asinValidation.valid ? '#91BE23' : '#ff4861' }}>
                {asinValidation.valid ? '✓' : '⚠'} ASIN Count: {asinValidation.asins.length}
              </div>
              {!asinValidation.valid && asinValidation.invalidAsins.length > 0 && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  Invalid ASINs (must start with B0 and be 10 characters): {asinValidation.invalidAsins.join(', ')}
                </div>
              )}
              {asinValidation.valid && asinValidation.asins.length > 0 && (
                <div style={{ fontSize: '0.9em', color: '#4a5568', marginTop: '4px' }}>
                  All ASINs are valid ✓
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Product IDs Field for Shopping only */}
      {fields?.productIds && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Product Item IDs{' '}
            <Tooltip
              id="googleProductIds"
              msg="Enter comma-separated Google Merchant Center product IDs (e.g., shopify_US_123_456)"
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="masterItemOnList"
              component={renderTextArea}
              format={itemCsvInput}
              placeholder="shopify_US_123_456, shopify_US_789_012"
            />
          </div>
          {formValues?.masterItemOnList && productIdValidation && (
            <div
              style={{
                marginTop: '10px',
                padding: '10px',
                borderRadius: '4px',
                border: `1px solid ${productIdValidation.valid ? 'rgba(145, 190, 35, 0.4)' : 'rgba(255, 165, 0, 0.4)'}`,
                backgroundColor: `${productIdValidation.valid ? 'rgba(145, 190, 35, 0.1)' : 'rgba(255, 165, 0, 0.1)'}`,
              }}
            >
              <div style={{ fontWeight: '500', color: productIdValidation.valid ? '#91BE23' : '#856404' }}>
                {productIdValidation.valid ? '✓' : 'ℹ️'} Product ID Count: {productIdValidation.count}
              </div>
              {productIdValidation.count > 0 && (
                <div style={{ fontSize: '0.9em', color: '#4a5568', marginTop: '4px' }}>
                  {productIdValidation.count} product {productIdValidation.count === 1 ? 'ID' : 'IDs'} entered
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Brand Name - Always shown for Google budgets */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Brand Name (Optional){' '}
          <Tooltip
            id="googleBrandName"
            msg="Enter the brand name associated with this budget. This will be used to identify the brand in budget reports."
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="googleBrandName"
            component={renderFieldForm}
            placeholder="Enter brand name"
          />
        </div>
      </div>

      {/* Sales Country - Always shown for Google budgets */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Sales Country{' '}
          <Tooltip
            id="googleSalesCountry"
            msg="Select the primary country where sales will be attributed"
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="googleSalesCountry"
            component={renderSelectField}
            options={GOOGLE_SALES_COUNTRY_OPTIONS}
          />
        </div>
      </div>

      {/* Language Constants - Required field for Google budgets */}
      <Field
        name="googleLanguages"
        component={GoogleConstantMultiSelect}
        label="Language Constants (Required)"
        tooltip="Search and select the languages to target for your Google ads"
        placeholder="Search languages (e.g., English, Spanish)..."
        searchFn={searchGoogleLanguageConstants}
        lookupByResourceNamesFn={getGoogleLanguageConstantsByResourceNames}
      />

      {/* Geo Targeting - All Google ad types */}
      <GeoTargetingFields
        setupFlow={setupFlow}
        agencyInternal={agencyInternal}
        supportsCities
        supportsStates
        supportsPostalCodes
      />
    </>
  );
};

GoogleBudgetFields.propTypes = {
  budgetTypeOption: PropTypes.shape({
    value: PropTypes.string,
    adType: PropTypes.string,
    itemType: PropTypes.string,
    fields: PropTypes.shape({
      url: PropTypes.bool,
      asins: PropTypes.bool,
      productIds: PropTypes.bool,
    }),
  }),
  setupFlow: PropTypes.shape({
    value: PropTypes.string,
  }),
  agencyInternal: PropTypes.shape({
    value: PropTypes.bool,
  }),
  formValues: PropTypes.shape({
    masterItemOnList: PropTypes.string,
    masterItemOffList: PropTypes.string,
  }),
  itemCsvInput: PropTypes.func.isRequired,
};

GoogleBudgetFields.defaultProps = {
  budgetTypeOption: null,
  setupFlow: null,
  agencyInternal: null,
  formValues: {},
};

export default GoogleBudgetFields;
